import { Controller } from "@hotwired/stimulus"

// Totem (kiosk) — fluxo step-by-step.
//
// Markup:
//   - Cada step de market tem 2 <input type="radio" name="picks[i][outcome_id]">
//     dentro do mesmo <form>. Browser nativo cuida do envio (sem JS no submit).
//   - JS apenas: avança visualmente entre steps quando radio é marcado.
//
// Hardening do quiosque:
//   - bloqueia teclas/atalhos que sairiam do site
//   - bloqueia context-menu, drag
//   - auto-fullscreen no primeiro toque
//   - reset por inatividade (3min)
export default class extends Controller {
  static targets = [ "step", "progressDot", "submitButton" ]
  static values = { mountedAt: Number }

  ADVANCE_DELAY_MS = 650
  IDLE_TIMEOUT_MS = 3 * 60 * 1000

  connect() {
    this.currentStep = 0
    this.submitting = false
    this._installKioskGuards()
    this._scheduleIdleReset()
  }

  disconnect() {
    this._uninstallKioskGuards()
    this._clearIdleTimer()
  }

  // Disparado quando user marca um radio (vote em uma outcome).
  voted(event) {
    if (this.submitting) return
    this._registerActivity()

    const radio = event.currentTarget
    const stepEl = radio.closest("[data-totem-target='step']")
    if (!stepEl) return

    // visual: a label do radio escolhido fica selected; outras fadeam
    const allLabels = stepEl.querySelectorAll(".totem__outcome")
    allLabels.forEach((lbl) => {
      const inner = lbl.querySelector("input[type='radio']")
      if (inner === radio) {
        lbl.classList.add("is-selected")
        lbl.classList.remove("is-faded")
      } else {
        lbl.classList.add("is-faded")
        lbl.classList.remove("is-selected")
      }
    })

    this._enterFullscreenOnce()

    // só avança se a step ativa é a do market que acabou de votar
    const stepIdx = parseInt(stepEl.dataset.stepIndex, 10)
    if (stepIdx === this.currentStep) {
      setTimeout(() => this._advance(), this.ADVANCE_DELAY_MS)
    }
  }

  submit(event) {
    if (this.submitting) {
      event.preventDefault()
      return
    }
    // Sanity check: o browser já valida required, mas confirmamos.
    const radios = this.element.querySelectorAll("input[type='radio'][name^='picks']")
    const groups = new Set()
    radios.forEach((r) => groups.add(r.name))
    const hasAllGroups = Array.from(groups).every((groupName) =>
      this.element.querySelector(`input[name='${groupName}']:checked`)
    )
    if (!hasAllGroups) {
      event.preventDefault()
      alert("Responda as 3 enquetes antes de enviar.")
      return
    }
    this._registerActivity()
    this.submitting = true
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = "Enviando…"
    }
    // form submete naturalmente
  }

  // ---------- Internals ----------
  _advance() {
    const next = this.currentStep + 1
    const totalSteps = this.stepTargets.length
    if (next >= totalSteps) return

    const currentEl = this.stepTargets[this.currentStep]
    const nextEl = this.stepTargets[next]
    if (!currentEl || !nextEl) return

    // Esconde atual instantaneamente (display:none), mostra próximo com animação CSS
    currentEl.classList.remove("is-active")
    nextEl.classList.add("is-active")
    window.scrollTo({ top: 0, behavior: "instant" })

    if (this.hasProgressDotTarget && this.progressDotTargets[this.currentStep]) {
      this.progressDotTargets[this.currentStep].classList.remove("is-active")
      this.progressDotTargets[this.currentStep].classList.add("is-done")
    }
    if (this.hasProgressDotTarget && this.progressDotTargets[next]) {
      this.progressDotTargets[next].classList.add("is-active")
    }
    this.currentStep = next
  }

  _registerActivity() {
    this._scheduleIdleReset()
  }

  _scheduleIdleReset() {
    this._clearIdleTimer()
    this._idleTimer = setTimeout(() => {
      if (this.submitting) return
      window.location.reload()
    }, this.IDLE_TIMEOUT_MS)
  }

  _clearIdleTimer() {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer)
      this._idleTimer = null
    }
  }

  _enterFullscreenOnce() {
    if (this._fullscreenAttempted) return
    this._fullscreenAttempted = true
    const el = document.documentElement
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen
    if (req) {
      try { req.call(el) } catch (_e) { /* ignora */ }
    }
  }

  _installKioskGuards() {
    this._onKeyDown = (e) => {
      const blocked = (
        e.key === "F11" || e.key === "F12" ||
        (e.ctrlKey && ["l","L","t","T","n","N","w","W","p","P","s","S","u","U"].includes(e.key)) ||
        (e.ctrlKey && e.shiftKey && ["i","I","j","J","c","C"].includes(e.key)) ||
        (e.metaKey && ["l","L","t","T","n","N","w","W","p","P","s","S"].includes(e.key)) ||
        (e.altKey && (e.key === "Tab" || e.key === "F4" || e.key === "ArrowLeft" || e.key === "ArrowRight"))
      )
      if (blocked) { e.preventDefault(); e.stopPropagation() }
    }
    this._onContextMenu = (e) => e.preventDefault()
    this._onDragStart = (e) => e.preventDefault()
    this._onActivity = () => this._registerActivity()

    document.addEventListener("keydown", this._onKeyDown, true)
    document.addEventListener("contextmenu", this._onContextMenu, true)
    document.addEventListener("dragstart", this._onDragStart, true)
    document.addEventListener("touchstart", this._onActivity, { passive: true })
    document.addEventListener("pointerdown", this._onActivity, { passive: true })
  }

  _uninstallKioskGuards() {
    if (this._onKeyDown) document.removeEventListener("keydown", this._onKeyDown, true)
    if (this._onContextMenu) document.removeEventListener("contextmenu", this._onContextMenu, true)
    if (this._onDragStart) document.removeEventListener("dragstart", this._onDragStart, true)
    if (this._onActivity) {
      document.removeEventListener("touchstart", this._onActivity)
      document.removeEventListener("pointerdown", this._onActivity)
    }
  }
}
